import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { bind } from 'bind-event-listener';

import { LS_ROVO_SIDEBAR_STATE_KEY } from '../../common/constants';

type RovoSidebarState = {
	isOpen: boolean;
};

const subscribe = (callback: () => void) => {
	return bind(window, {
		type: 'rovo-sidebar-state-changed',
		listener: callback,
	});
};

// Get the current state from localStorage
const getSnapshot = (): string | null => {
	return localStorage.getItem(LS_ROVO_SIDEBAR_STATE_KEY);
};

// Set the state in localStorage
const setSnapshot = (isOpen: boolean): void => {
	try {
		const value: RovoSidebarState = { isOpen };
		localStorage.setItem(LS_ROVO_SIDEBAR_STATE_KEY, JSON.stringify(value));
		window.dispatchEvent(new Event('rovo-sidebar-state-changed'));
	} catch {
		// Silently fail
	}
};

/**
 * Hook to read and manage the Rovo sidebar open state from localStorage
 * Provides cross-tab synchronization through storage events
 */
export const useRovoSidebar = (): {
    isOpen: boolean;
    close: () => void;
    toggle: () => void;
} => {
	const store = useSyncExternalStore(subscribe, getSnapshot);
	const { isOpen } = useMemo(() => {
		if (!store) {
			return { isOpen: false };
		}
		const parsed = JSON.parse(store) as RovoSidebarState;
		return parsed;
	}, [store]);

	const close = useCallback((): void => {
		setSnapshot(false);
	}, []);

	const toggle = useCallback((): void => {
		setSnapshot(!isOpen);
	}, [isOpen]);

	return {
		isOpen,
		close,
		toggle,
	};
};
